import React, { Suspense } from 'react';
import './dyf-landing-view.scss';
import { DyfFeatureGuideContentProps } from './hooks/use-dyf-feature-guide-content';
import { EligibleModelData } from '../../hooks/dyf/use-dyf-eligible-model/use-dyf-eligible-model';
import { SearchContentProps } from '../../components/sections/search-bar/hooks/use-search-content';
import { FeatureGuideBillboard } from '../../components/sections/dyf-landing/feature-guide-billboard/feature-guide-billboard.ford';
import DyfFeatureRibbon from '../../components/sections/onboarding-global-components/onboarding-feature-ribbon/dyf-feature-ribbon';
import SearchBarInput from '../../components/sections/search-bar/search-bar-input/search-bar-input';
import { ProfileWithVehiclesResponse } from '../../models/profile-with-vehicles';
import { ShortcodeProviders } from '../../services/shortcode-service/shortcode-service';
import { VehicleAttributes } from '../../models/vehicle-attributes';
import { VehicleYmm } from '../../models/vehicle-ymm';
import { DyfFeatureGuideBreadCrumbs } from '../../components/sections/dyf-landing/dyf-feature-guide-breadcrumbs/dyf-feature-guide-breadcrumbs';
import { DyfFeatureGuideBreadcrumbAemContent } from './hooks/use-dyf-feature-guide-breadcrumbs-content';
import { OnboardingVehicleSelector } from '../../components/sections/onboarding-global-components/onboarding-vehicle-selector/onboarding-vehicle-selector';
import { ActivityIndicator } from '../../components/common/activity-indicator/activity-indicator';
import { FeatureGuideCards } from '../../components/sections/dyf-landing/feature-guide-cards/feature-guide-cards';

const YourVehicleFeatures = React.lazy(() =>
    import(
        '../../components/sections/onboarding-global-components/your-vehicle-features/your-vehicle-features'
    )
);
const DyfOwnersManual5050 = React.lazy(() =>
    import(
        '../../components/sections/dyf-landing/feature-guide-cards/5050-cards/dyf-owners-manual-5050/dyf-owners-manual-5050'
    ).then(module => ({
        default: module.DyfOwnersManual5050,
    }))
);
const VideoTiles = React.lazy(() =>
    import(
        '../../components/sections/onboarding-global-components/video-tiles/video-tiles'
    ).then(module => ({
        default: module.VideoTiles,
    }))
);

interface DyfLandingFordComponentProps {
    vehicleYmmGroup: VehicleYmm | null;
    featureGuideContent: DyfFeatureGuideContentProps | null;
    eligibleModelData: EligibleModelData | null;
    vin: string | null;
    shouldShowFeatureRibbon: boolean;
    searchContent: SearchContentProps | null;
    userGuid: string;
    profile: ProfileWithVehiclesResponse | undefined;
    shouldShowOwnersManual5050: boolean;
    baseRoot: string;
    showYourVehicleFeatures: boolean;
    shouldShowSearchBar: boolean;
    shouldShowFeatureGuideCards: boolean;
    shouldShowVehicleSelector: boolean;
    vehicleAttributes: any;
    currentLanguageRegionCode: string;
    handleVehicleSelectorOnChange: (
        title: string,
        index: number,
        value: string,
        match: VehicleAttributes
    ) => void;
    flow: 'ymm' | 'vin';
    fireEvents: (
        eventNames: string | string[],
        targetViewName?: string,
        shortcodeProviders?: ShortcodeProviders,
        clearDigitaldata?: boolean,
        skipFireTargetAnalytics?: boolean
    ) => void;
    dyfLandingBreadcrumbs: DyfFeatureGuideBreadcrumbAemContent;
    vehicleImage: string | null;
    isMobile?: boolean;
}

export const DyfLandingViewFord = (props: DyfLandingFordComponentProps) => {
    const {
        vehicleYmmGroup,
        featureGuideContent,
        eligibleModelData,
        vin,
        shouldShowFeatureRibbon,
        shouldShowOwnersManual5050,
        shouldShowFeatureGuideCards,
        searchContent,
        baseRoot,
        vehicleAttributes,
        userGuid,
        profile,
        showYourVehicleFeatures,
        handleVehicleSelectorOnChange,
        shouldShowSearchBar,
        fireEvents,
        currentLanguageRegionCode,
        dyfLandingBreadcrumbs,
        flow,
        shouldShowVehicleSelector,
        vehicleImage,
        isMobile,
    } = props;

    return (
        <>
            <Suspense fallback={<ActivityIndicator />}>
                {shouldShowVehicleSelector ? (
                    <div className="dyf-vehicle-selector">
                        <OnboardingVehicleSelector
                            vehicleYmmGroup={{
                                year: vehicleYmmGroup!.year,
                                make: vehicleYmmGroup!.make,
                                model: vehicleYmmGroup!.model,
                            }}
                            vin={vin ?? undefined}
                            onChange={handleVehicleSelectorOnChange}
                            page={'DYF Feature Guide'}
                            disableDashboardRedirect={true}
                        />
                    </div>
                ) : (
                    <div className="dyf-vehicle-selector"></div>
                )}

                <div
                    className="dyf-landing-page-breadcrumbs"
                    data-testid="dyf-landing-page-breadcrumbs"
                >
                    {dyfLandingBreadcrumbs ? (
                        <DyfFeatureGuideBreadCrumbs
                            origin={'landing'}
                            dyfFeatureGuideBreadcrumbs={dyfLandingBreadcrumbs}
                            vinDetails={
                                flow == 'vin'
                                    ? {
                                          vehicleName:
                                              vehicleYmmGroup?.model || '',
                                          vin: vin!,
                                      }
                                    : null
                            }
                            ymmDetails={
                                flow == 'ymm'
                                    ? {
                                          modelName:
                                              eligibleModelData?.modelDisplayName ||
                                              vehicleYmmGroup?.model ||
                                              '',
                                      }
                                    : null
                            }
                        />
                    ) : (
                        <>
                            <div className="dyf-landing-page-breadcrumbs" />
                        </>
                    )}
                </div>

                <div
                    className={`onboarding-landing--section feature-guide-billboard feature-guide-billboard--${flow}`}
                >
                    {eligibleModelData && featureGuideContent ? (
                        <FeatureGuideBillboard
                            eligibleFeatureGuide={
                                eligibleModelData?.eligibleFeatureGuide
                            }
                            vin={vin || null}
                            eligibleModelData={eligibleModelData}
                            featureGuideContent={featureGuideContent!}
                            flow={flow}
                            profile={profile}
                            vehicleImage={vehicleImage}
                            isMobile={isMobile}
                        />
                    ) : (
                        <></>
                    )}
                </div>
            </Suspense>

            <div className="onboarding-landing--section">
                {shouldShowFeatureRibbon && eligibleModelData && (
                    <DyfFeatureRibbon
                        vin={vin!}
                        featureGuideContent={featureGuideContent!}
                        vehicleType={eligibleModelData!.vehicleType}
                        eligibleFeatureGuide={
                            eligibleModelData!.eligibleFeatureGuide!
                        }
                        eligibleTowingCalculator={
                            eligibleModelData!.eligibleTowingCalculator!
                        }
                        vehicleYmmGroup={vehicleYmmGroup}
                    />
                )}
            </div>
            {shouldShowSearchBar && (
                <div
                    className="dyf-search--wrap"
                    data-testid={'dyf-search--wrap'}
                >
                    <SearchBarInput
                        hideSearchBarTitle={true}
                        searchContent={searchContent!}
                    />
                </div>
            )}

            <div className="onboarding-landing--section onboarding-feature-guide-cards-section">
                {shouldShowFeatureGuideCards && (
                    <Suspense>
                        <FeatureGuideCards
                            eligibleModelData={eligibleModelData!}
                            discoverYourFordText={
                                featureGuideContent?.billboardBody ?? ''
                            }
                            isMobile={isMobile}
                        />
                    </Suspense>
                )}
            </div>

            <div className="onboarding-landing--section">
                {showYourVehicleFeatures && featureGuideContent && (
                    <Suspense>
                        <YourVehicleFeatures
                            languageRegionCode={currentLanguageRegionCode}
                            flow={flow}
                            featureGuideContent={featureGuideContent!}
                            vin={vin}
                            userGuid={userGuid}
                            profile={profile}
                        />
                    </Suspense>
                )}
            </div>

            <div className="onboarding-landing--section onboarding-feature-guide-cards-section">
                {shouldShowOwnersManual5050 && (
                    <Suspense>
                        <DyfOwnersManual5050
                            discoverYourFordText={
                                featureGuideContent?.billboardBody ?? ''
                            }
                            featureGuideContent={featureGuideContent!}
                            baseRoot={baseRoot}
                            vehicleSeoKey={vehicleYmmGroup!.seoKey}
                            vehicleYear={`${vehicleYmmGroup!.year}`}
                            onCardClick={() =>
                                fireEvents(
                                    'dyf-feature-guide-owner-manual-cta-onclick',
                                    undefined,
                                    undefined
                                )
                            }
                        />
                    </Suspense>
                )}
            </div>

            {vehicleYmmGroup?.year &&
                vehicleYmmGroup?.make &&
                vehicleYmmGroup?.marketName && (
                    <Suspense>
                        <VideoTiles
                            vehicleAttributes={{
                                year: vehicleYmmGroup?.year,
                                make: vehicleYmmGroup?.make,
                                model: vehicleYmmGroup?.marketName,
                                seoKey: vehicleYmmGroup.seoKey,
                            }}
                            seoKey={vehicleAttributes.seoKey}
                            origin={''}
                        />
                    </Suspense>
                )}
        </>
    );
};
